'use client';

import { useForm } from 'react-hook-form';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Input,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Alert
} from '@goodneighbor/ui';
import { resetPassword } from '@/services/webApi';
import { useMutation } from '@tanstack/react-query';
import styles from './resetPasswordContent.module.scss';
import { useSearchParams } from 'next/navigation';
import { passwordSchema } from '@goodneighbor/api/schemas/auth';
import Link from 'next/link';

export const ResetFormSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordSchema
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword']
  });

export type ResetFormValues = z.infer<typeof ResetFormSchema>;

export default function ResetPasswordForm(props: {
  email?: string;
  token?: string;
  resetSuccessUrl?: string;
}) {
  const resetSuccessUrl = props.resetSuccessUrl || '/partner';
  const searchParams = useSearchParams();
  const email = props.email || searchParams.get('email') || '';
  const token = props.token || searchParams.get('token') || '';

  const mutation = useMutation({
    mutationFn: async (vars: ResetFormValues) => {
      return resetPassword(
        {
          password: vars.password
        },
        { email, token }
      );
    }
  });

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(ResetFormSchema),
    defaultValues: {
      confirmPassword: '',
      password: ''
    }
  });

  const onSubmit = (data: ResetFormValues) => {
    mutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form
        method="post"
        className={styles.form}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className={styles.textField}>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Password"
                  showPasswordToggle
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem className={styles.textField}>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Confirm Password"
                  showPasswordToggle
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          className={styles.submitButton}
          type="submit"
          isLoading={mutation.isPending}
          disabled={!form.formState.isValid}
          size="lg"
        >
          Change your password
        </Button>
        {mutation.isSuccess && (
          <Alert
            className={styles.infoCard}
            color="success"
            title="Success"
            description={
              <>
                Your password has been changed successfully. You can now{' '}
                <Link className={styles.link} href={resetSuccessUrl}>
                  log in
                </Link>
                .
              </>
            }
          />
        )}
        {mutation.isError && (
          <Alert
            className={styles.infoCard}
            color="error"
            title="Something went wrong"
            description={mutation.error.message}
          />
        )}
      </form>
    </Form>
  );
}
